/**
 * Design-theme UI state — one store per open board view, owned by BoardScreen,
 * exactly like the comments store (`features/comments/store.ts`).
 *
 * Everything here is LOCAL view state: whether the panel is open, whether a
 * generation is in flight, and whether the replace-confirm is showing. The art
 * direction itself lives in the Yjs doc and is read reactively through the
 * session's `designVersion`.
 *
 * NOTE on `registries/panels/`: docs/08 §1 sketches a panel registry, but it
 * does not exist — `registries/` holds `element-types/` and `toolbar-actions/`
 * only. The real pattern for a sidebar surface in this codebase is
 * store-owned-by-BoardScreen + a panel component, which is what this follows.
 * Inventing a registry for a single consumer would be indirection nothing else
 * uses.
 */
import { createStore, type StoreApi } from "zustand/vanilla";

export interface DesignUiState {
  /** The design sidebar panel. */
  panelOpen: boolean;
  /** An art-direction generation is in flight. */
  generating: boolean;
  /** An image RUN is being started (the 202 has not come back yet). */
  startingImages: boolean;
  /**
   * A generation is waiting on the user to confirm it may replace the existing
   * art direction. Holds nothing but the intent — the request has not been
   * sent, so cancelling costs nothing.
   */
  confirmReplace: boolean;
  /**
   * Last ART-DIRECTION error. Separate from `imageError` because the two
   * controls sit far apart in a scrolling panel: one shared slot put the image
   * failure up beside the art-direction button, where it reads as the wrong
   * thing having failed — and off-screen from the button actually pressed.
   */
  error: string | null;
  /** Last KEY-VISUAL error, shown beside the key-visual controls. */
  imageError: string | null;

  setPanelOpen(open: boolean): void;
  setGenerating(generating: boolean): void;
  setStartingImages(starting: boolean): void;
  setConfirmReplace(confirm: boolean): void;
  setError(error: string | null): void;
  setImageError(error: string | null): void;
}

export type DesignUiStore = StoreApi<DesignUiState>;

export function createDesignUiStore(): DesignUiStore {
  return createStore<DesignUiState>((set) => ({
    panelOpen: false,
    generating: false,
    startingImages: false,
    confirmReplace: false,
    error: null,
    imageError: null,

    setPanelOpen: (panelOpen) => set({ panelOpen }),
    setGenerating: (generating) => set({ generating }),
    setStartingImages: (startingImages) => set({ startingImages }),
    setConfirmReplace: (confirmReplace) => set({ confirmReplace }),
    setError: (error) => set({ error }),
    setImageError: (imageError) => set({ imageError }),
  }));
}
